import { ButtonBase, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import { useConversion } from '@hooks/useConversion';

import { getUnits } from '@utils/conversion';

const messages = defineMessages({
  switch: {
    id: 'unitPill.switch',
    defaultMessage: 'Switch units',
  },
});

export interface UnitPillProps {
  /** Conversion group, normally the vital sign code. */
  code: string;
  disabled?: boolean;
  /** Matches the value type it sits beside. */
  size?: 'medium' | 'small';
}

/**
 * The unit a value is shown in, and a tap to swap it.
 *
 * Replaces the two-label {@link Selector} on surfaces where the toggle sat on
 * a line of its own and pushed the reading around. Only the unit in use is
 * shown, so it takes one line beside the value instead of a row beneath it.
 *
 * Renders nothing for a code with no alternate unit, which is most of them.
 */
export default function UnitPill({
  code,
  disabled = false,
  size = 'medium',
}: UnitPillProps) {
  const { formatMessage } = useIntl();
  const {
    convert,
    state: { conversion },
  } = useConversion();

  const units = getUnits(code);
  if (!units.SI || !units.CON) {
    return null;
  }

  const isConverted = conversion[code] ?? false;

  return (
    <ButtonBase
      aria-label={formatMessage(messages.switch)}
      data-testid={`unit-pill-${code}`}
      disabled={disabled}
      disableRipple
      onClick={(event) => {
        // The card behind this is itself a link to the drilldown.
        event.stopPropagation();
        convert({ toggleType: code });
      }}
      sx={({ palette }) => ({
        alignItems: 'center',
        backgroundColor: palette.grey[50],
        border: `1px solid ${palette.divider}`,
        borderRadius: 999,
        color: palette.text.secondary,
        gap: 0.5,
        lineHeight: 1,
        px: 1,
        py: 0.375,
        '&:hover': {
          backgroundColor: palette.common.accentLightblue,
          borderColor: palette.primary.main,
          color: palette.primary.main,
        },
      })}
      title={formatMessage(messages.switch)}
    >
      <Typography
        color="inherit"
        lineHeight={1}
        variant={size === 'small' ? 'caption' : 'subtitle2'}
      >
        {isConverted ? units.CON : units.SI}
      </Typography>
      <Icon color="inherit" name="right-left" size="2xs" type="fas" />
    </ButtonBase>
  );
}
